import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@shared/store/authStore';
import { SMSCodeInput } from '../components/SMSCodeInput';
import { PhoneInput } from '../components/PhoneInput';

const registerSchema = z.object({
  firstName: z.string()
    .min(2, 'Имя должно содержать минимум 2 символа')
    .max(50, 'Имя слишком длинное'),
  lastName: z.string()
    .min(2, 'Фамилия должна содержать минимум 2 символа')
    .max(50, 'Фамилия слишком длинная'),
  middleName: z.string().optional(),
  phone: z.string()
    .min(10, 'Введите корректный номер телефона')
    .regex(/^[+]?[7-8]?\d{10}$/, 'Неверный формат номера'),
  email: z.string()
    .email('Неверный формат email')
    .optional()
    .or(z.literal('')),
  birthday: z.string().optional(),
  agreement: z.boolean().refine(val => val === true, {
    message: 'Необходимо принять условия пользования'
  })
});

type RegisterForm = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const { 
    isAuthenticated, 
    register: registerUser, 
    isLoading,
    sendSMSCode,
    registerWithSMS 
  } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<'form' | 'sms'>('form');
  const [smsCode, setSmsCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      phone: location.state?.phone || ''
    }
  });

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Get phone from location state if available
  const phoneFromLocation = location.state?.phone;

  // Countdown timer for SMS resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const onSubmit = async (data: RegisterForm) => {
    setError('');
    try {
      // Normalize phone number
      const normalizedPhone = data.phone.replace(/[^\d]/g, '');
      
      // Send SMS code for verification
      await sendSMSCode(normalizedPhone);
      setStep('sms');
      setCountdown(60);
    } catch (error) {
      setError('Ошибка при отправке SMS. Попробуйте снова.');
    }
  };

  const handleSMSComplete = async (code: string) => {
    setError('');
    try {
      // Register with SMS verification and login
      const formData = getValues();
      const phone = formData.phone.replace(/[^\d]/g, '');
      
      await registerWithSMS(phone, code, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName,
        email: formData.email,
        birthday: formData.birthday
      });
      
      // User will be automatically redirected by auth state change
    } catch (error) {
      setError('Ошибка регистрации. Попробуйте снова.');
    }
  };

  const handleResendSMS = async () => {
    try {
      const phone = getValues('phone').replace(/[^\d]/g, '');
      await sendSMSCode(phone);
      setCountdown(60);
      setError('');
    } catch (error) {
      setError('Ошибка при отправке SMS. Попробуйте снова.');
    }
  };

  if (step === 'sms') {
    const phone = getValues('phone').replace(/[^\d]/g, '');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-600 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg">
              <span className="text-white font-bold text-2xl">LSC</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Подтверждение регистрации</h1>
            <p className="text-gray-600">Введите код из SMS для завершения</p>
          </div>

          {/* SMS Verification */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-6">
              <SMSCodeInput
                onComplete={handleSMSComplete}
                onResend={handleResendSMS}
                isLoading={isLoading}
                phone={phone}
                countdown={countdown}
              />
              
              {error && (
                <div className="text-center">
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={() => setStep('form')}
                className="w-full text-gray-600 hover:text-gray-800 py-2 text-sm"
              >
                ← Изменить данные регистрации
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-emerald-600 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">LSC</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Регистрация</h1>
          <p className="text-gray-600">Станьте членом Level Sport Club</p>
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Name Fields */}
            {error && (
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('firstName')}
                  type="text"
                  placeholder="Анна"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Фамилия <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('lastName')}
                  type="text"
                  placeholder="Петрова"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* Middle Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Отчество
              </label>
              <input
                {...register('middleName')}
                type="text"
                placeholder="Александровна"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Номер телефона <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                {...register('phone')}
                placeholder="XXX (XXX) XX-XX"
                error={errors.phone?.message}
                className="text-sm"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                {...register('email')}
                type="email"
                placeholder="anna@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Birthday */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата рождения
              </label>
              <input
                {...register('birthday')}
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
              />
            </div>

            {/* Agreement */}
            <div className="flex items-start space-x-3">
              <input
                {...register('agreement')}
                type="checkbox"
                className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <div className="text-sm">
                <label className="text-gray-700">
                  Я согласен с{' '}
                  <a href="#" className="text-emerald-600 hover:text-emerald-700">
                    условиями пользования
                  </a>{' '}
                  и{' '}
                  <a href="#" className="text-emerald-600 hover:text-emerald-700">
                    политикой конфиденциальности
                  </a>
                </label>
                {errors.agreement && (
                  <p className="text-red-500 text-xs mt-1">{errors.agreement.message}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-6"
            >
              {isLoading ? (
                <>
                  <div className="spinner mr-2"></div>
                  Регистрируем...
                </>
              ) : (
                'Зарегистрироваться'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Уже есть аккаунт?{' '}
              <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
                Войти
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}